import { useTranslation } from 'react-i18next';
import { Clock } from 'lucide-react';
import { Card, CardContent } from '@/shared/components/ui/card';
import { formatDisplayDate, parseApiDate } from '@/shared/utils/dateUtils';

interface PublicFormClosedCardProps {
  title: string;
  /** When present and in the past, shows the specific expiry date instead of a generic message. */
  expiresAt?: string | null;
}

export function PublicFormClosedCard({ title, expiresAt }: PublicFormClosedCardProps) {
  const { t } = useTranslation('forms');
  // The parent only renders this card once the form is already closed (by status
  // or expiration), so any `expiresAt` present is shown as-is — no client-side
  // "is it actually past" check needed (and `Date.now()` would be impure in render).
  const expiryDate = parseApiDate(expiresAt);

  return (
    <Card className="w-full max-w-lg mx-auto" data-testid="forms-public-closed-message">
      <CardContent className="flex flex-col items-center text-center gap-4 py-10">
        <div className="rounded-full bg-muted p-4">
          <Clock className="h-6 w-6 text-muted-foreground" />
        </div>
        <div className="space-y-2">
          <h1 className="text-base font-semibold md:text-lg">{t('renderer.closed.title')}</h1>
          <p className="text-sm text-muted-foreground">
            {expiryDate
              ? t('renderer.closed.descriptionWithDate', {
                  title,
                  date: formatDisplayDate(expiryDate),
                })
              : t('renderer.closed.description', { title })}
          </p>
        </div>
        <p className="text-xs text-muted-foreground">{t('renderer.closed.poweredBy')}</p>
      </CardContent>
    </Card>
  );
}
